import { FC, useState } from 'react';
import { createUseStyles } from 'react-jss';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { useSubscription } from '@meraki-internal/state';
import { CareMapState } from '../../caremap/CareMapState';
import { CommunityModelFactory } from '../../share-caremap/CommunityModelFactory';
import { BottomSheetModal } from '../../components/BottomSheetModal';
import { HeaderPopover } from '../../components/HeaderPopover';
import { ViewShareLinkButton } from '../../share-caremap/ViewShareLinkButton';
import {
    countCoOwners,
    getPresetPeople,
    IAccessLevel,
    IHivePreset,
    IPersonRow,
    IPrototypePerson,
    mockPersonToRow
} from './prototypeData';
import { AddPersonFlow } from './AddPersonFlow';
import { PeopleList, RealHivePeopleList } from './PrototypePeopleList';
import { PrototypeCareMapEditDrawer } from './PrototypeCareMapEditDrawer';

// ─────────────────────────────────────────────────────────────────────────────
// IH-6912 spike — Sharing & Access sheet
// Throwaway. Lives under _prototype/. Gated by devSettings.experimentalUnifiedSharing.
// ─────────────────────────────────────────────────────────────────────────────

const useStyles = createUseStyles({
    sheetWrap: {
        display: 'flex',
        flexDirection: 'column',
        maxHeight: 'min(90vh, 760px)',
        background: '#fff'
    },
    scrollArea: {
        padding: '20px 22px 16px',
        overflowY: 'auto',
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        gap: 14
    },

    headerRow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
    },
    title: {
        fontSize: 20,
        fontWeight: 700,
        color: 'var(--ion-text-color)'
    },
    closeBtn: {
        background: 'none',
        border: 0,
        color: 'var(--ion-color-medium)',
        fontSize: 18,
        cursor: 'pointer',
        padding: 4
    },
    sectionLabel: {
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        color: 'var(--ion-color-step-400)',
        marginTop: 4
    },
    segmentedButton: {
        display: 'flex',
        background: 'var(--ion-color-step-50)',
        borderRadius: 8,
        padding: 3,
        gap: 2
    },
    segmentBtn: {
        flex: 1,
        background: 'transparent',
        border: 0,
        padding: '8px 12px',
        fontSize: 13,
        fontWeight: 600,
        color: 'var(--ion-color-step-750)',
        borderRadius: 6,
        cursor: 'pointer'
    },
    segmentBtnActive: {
        background: '#fff',
        color: 'var(--ion-color-primary)',
        boxShadow: '0 1px 2px rgba(0,0,0,0.08)'
    },
    visibilityStateDesc: {
        fontSize: 14,
        color: 'var(--ion-color-step-750)',
        lineHeight: 1.45
    },
    divider: {
        height: 1,
        background: 'var(--ion-color-step-100)'
    },
    emptyListNote: {
        fontSize: 14,
        color: 'var(--ion-color-step-750)',
        lineHeight: 1.45
    },
    // Small outline pill that toggles the share-link UI open and closed —
    // keeps the share section out of the way unless the user explicitly opens it.
    shareLinkToggle: {
        background: '#fff',
        color: 'var(--ion-color-primary)',
        border: '1px solid var(--ion-color-primary)',
        borderRadius: 8,
        padding: '8px 14px',
        fontSize: 13,
        fontWeight: 600,
        cursor: 'pointer',
        alignSelf: 'flex-start',
        marginTop: 8
    },
    // The real ViewShareLinkButton renders its description as a plain <p> with
    // default size + bold spans. Wrap it to match the other two description
    // texts in this sheet (14px, step-750, 1.45 line-height).
    shareLinkWrap: {
        '& p': {
            fontSize: '14px !important',
            color: 'var(--ion-color-step-750) !important',
            lineHeight: 1.45,
            margin: '10px 0 0 !important'
        },
        '& p strong': {
            color: 'var(--ion-text-color)'
        }
    },

    // People-with-access header row: title + small info button
    peopleHeader: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        marginTop: 4
    },
    accessSummary: {
        fontSize: 14,
        color: 'var(--ion-color-step-750)',
        lineHeight: 1.45
    },
    infoBtn: {
        width: 18,
        height: 18,
        borderRadius: '50%',
        background: 'var(--ion-color-step-100)',
        color: 'var(--ion-color-step-750)',
        border: 0,
        fontSize: 11,
        fontWeight: 700,
        fontStyle: 'italic',
        fontFamily: 'Georgia, serif',
        cursor: 'pointer',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0
    },
    popoverBody: {
        padding: '14px 16px',
        fontSize: 13,
        color: 'var(--ion-color-step-750)',
        lineHeight: 1.5
    },
    popoverRole: {
        fontWeight: 700,
        color: 'var(--ion-text-color)'
    },

    listActionFooter: {
        position: 'sticky',
        bottom: 0,
        background: '#fff',
        paddingTop: 12,
        paddingBottom: 4,
        marginTop: 8,
        borderTop: '1px solid var(--ion-color-step-100)',
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        zIndex: 1
    },
    primaryBtn: {
        background: 'var(--ion-color-primary)',
        color: '#fff',
        border: 0,
        borderRadius: 8,
        padding: '11px',
        fontSize: 14,
        fontWeight: 700,
        cursor: 'pointer'
    },
    outlineBtn: {
        background: '#fff',
        color: 'var(--ion-color-primary)',
        border: '1px solid var(--ion-color-primary)',
        borderRadius: 8,
        padding: '10px',
        fontSize: 13,
        fontWeight: 600,
        cursor: 'pointer'
    },

    // Spike controls footer — always visible, subdued styling so the real UI above reads as real.
    spikeFooter: {
        flexShrink: 0,
        borderTop: '1px solid var(--ion-color-step-100)',
        background: 'var(--ion-color-step-50)',
        padding: '6px 12px',
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        flexWrap: 'wrap'
    },
    spikeBadge: {
        fontSize: 9,
        fontWeight: 700,
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        color: '#7a5b00',
        background: '#fff3cd',
        padding: '2px 6px',
        borderRadius: 3
    },
    miniSwitcher: {
        display: 'flex',
        gap: 1,
        padding: 1,
        background: '#fff',
        border: '1px solid var(--ion-color-step-150)',
        borderRadius: 4
    },
    miniBtn: {
        background: 'transparent',
        border: 0,
        padding: '3px 7px',
        fontSize: 11,
        fontWeight: 600,
        color: 'var(--ion-color-medium)',
        borderRadius: 3,
        cursor: 'pointer'
    },
    miniBtnOn: {
        background: 'var(--ion-color-primary)',
        color: '#fff'
    }
});

export interface ISharingAndAccessSheetProps {
    isOpen: boolean;
    onDidDismiss: () => void;
}

export const SharingAndAccessSheet: FC<ISharingAndAccessSheetProps> = ({ isOpen, onDidDismiss }) => {
    const classes = useStyles();
    const careMapState = useInstance(CareMapState);
    useSubscription(() => careMapState);

    const communityFactory = useInstance(CommunityModelFactory);
    useSubscription(() => communityFactory);
    const community = communityFactory.forActiveCaremap();

    const [page, setPage] = useState<'list' | 'add-person' | 'edit-care-map'>('list');
    const [editingRow, setEditingRow] = useState<IPersonRow | undefined>(undefined);
    // 'populated' = live data (default). 'brand-new' = empty mock. 'full' = busy mock hive.
    const [preset, setPreset] = useState<IHivePreset>('populated');
    const [people, setPeople] = useState<IPrototypePerson[]>(() => getPresetPeople('brand-new'));
    const [showAccessInfo, setShowAccessInfo] = useState(false);
    const [showShareLink, setShowShareLink] = useState(false);

    const applyPreset = (next: IHivePreset) => {
        setPreset(next);
        // 'populated' uses live data via RealHivePeopleList — don't touch local people.
        // 'brand-new' / 'full' use local mock data — reset the array to the preset.
        if (next !== 'populated') {
            setPeople(getPresetPeople(next));
        }
    };

    // Spike-only: native browser dialogs for stub actions and confirm. The real
    // build would use AlertPresenter / Toast and a proper destructive-confirm.
    const stubAlert = (message: string) => {
        // eslint-disable-next-line no-alert
        window.alert(message);
    };
    const confirmRemove = (label: string): Promise<boolean> => {
        // eslint-disable-next-line no-alert
        return Promise.resolve(window.confirm(`Remove ${label} from this care recipient?`));
    };

    const removeMockPerson = (id: string) => async () => {
        setPeople(prev => prev.filter(p => p.id !== id));
    };

    // Opens the fake care-map edit drawer for a given row. Bound onto rows
    // so the menu item can fire it. The drawer reads back via editingRow state.
    const openCareMapEdit = (row: IPersonRow) => {
        setEditingRow(row);
        setPage('edit-care-map');
    };

    const closeCareMapEdit = () => {
        setEditingRow(undefined);
        setPage('list');
    };

    const caremap = careMapState.getActiveCaremap();
    const patient = caremap?.patient;

    if (!patient) {
        return null;
    }

    const isShared = patient.notesIsPublic === true;
    const coOwnersUsed = countCoOwners(people);

    // Whether the default-visibility section's description should describe
    // the consequence ("visible to viewers / hidden from viewers") or just
    // say "No viewers added yet" — depends on which preset we're showing.
    const hasViewers = preset === 'populated'
        ? community.isLoaded() && (community.getPermanentViewers().length > 0 || community.getInvites('view-only').length > 0)
        : people.some(p => p.access === 'viewer');

    const handleClose = () => {
        setPage('list');
        setShowAccessInfo(false);
        setShowShareLink(false);
        onDidDismiss();
    };

    const updateVisibilityDefault = (shared: boolean) => {
        if (shared !== isShared) {
            careMapState.updatePatient({ ...patient, notesIsPublic: shared });
        }
    };

    const handleAddPerson = (person: Omit<IPrototypePerson, 'id' | 'status'>) => {
        if (preset === 'populated') {
            stubAlert(`Stub: would invite ${person.firstName} ${person.lastName} as ${person.access === 'co-owner' ? 'co-owner' : 'viewer'}.`);
        } else {
            const next: IPrototypePerson = {
                ...person,
                id: `p${Date.now()}`,
                status: 'pending',
                addedAt: new Date().toISOString()
            };
            setPeople(prev => [...prev, next]);
        }
        setPage('list');
    };

    const mockRows: IPersonRow[] = people.map(p => {
        const row = mockPersonToRow(p, removeMockPerson(p.id));
        // Only people actually on the care map get the "Update in Care Map" menu item.
        if (row.careMapRole) {
            row.onUpdateInCareMap = () => openCareMapEdit(row);
        }
        return row;
    });

    return (
        <>
            <BottomSheetModal
                isOpen={isOpen}
                onDidDismiss={handleClose}
                data-id="sharing-and-access-sheet"
            >
                <div className={classes.sheetWrap}>
                    <div className={classes.scrollArea}>
                        {page === 'list' && (
                            <>
                                <div className={classes.headerRow}>
                                    <div className={classes.title}>Sharing &amp; Access</div>
                                    <button className={classes.closeBtn} onClick={handleClose} aria-label="Close">✕</button>
                                </div>

                                <div className={classes.peopleHeader}>
                                    <div className={classes.sectionLabel} style={{ marginTop: 0 }}>People with access</div>
                                    <button
                                        id="access-info-trigger"
                                        className={classes.infoBtn}
                                        onClick={() => setShowAccessInfo(v => !v)}
                                        aria-label="About access levels"
                                    >
                                        i
                                    </button>
                                </div>
                                {(preset === 'populated' || mockRows.length > 0) && (
                                    <div className={classes.accessSummary}>
                                        Viewers see shared notes and recordings. Co-Owners have full access and can invite others.
                                    </div>
                                )}

                                {preset === 'populated' ? (
                                    <RealHivePeopleList
                                        onStubAction={stubAlert}
                                        confirmRemove={confirmRemove}
                                    />
                                ) : (
                                    <>
                                        {mockRows.length === 0 && (
                                            <div className={classes.emptyListNote}>
                                                No one else has access yet. Invite others when you're ready to start collaborating!
                                            </div>
                                        )}
                                        {mockRows.length > 0 && (
                                            <PeopleList
                                                rows={mockRows}
                                                onStubAction={stubAlert}
                                                confirmRemove={confirmRemove}
                                            />
                                        )}
                                    </>
                                )}

                                <div className={classes.sectionLabel}>Share Temporary Access</div>
                                {showShareLink && (
                                    <div className={classes.shareLinkWrap}>
                                        <ViewShareLinkButton trackingFrom="Sharing Set Up" />
                                    </div>
                                )}
                                <button
                                    className={classes.shareLinkToggle}
                                    onClick={() => setShowShareLink(v => !v)}
                                >
                                    {showShareLink ? 'Done' : '+ Create Link'}
                                </button>

                                <div className={classes.divider} />

                                <div className={classes.sectionLabel}>Default Note &amp; Recording Access</div>
                                <div className={classes.segmentedButton}>
                                    <button
                                        className={`${classes.segmentBtn} ${isShared ? classes.segmentBtnActive : ''}`}
                                        onClick={() => updateVisibilityDefault(true)}
                                    >
                                        Shared with Viewers
                                    </button>
                                    <button
                                        className={`${classes.segmentBtn} ${!isShared ? classes.segmentBtnActive : ''}`}
                                        onClick={() => updateVisibilityDefault(false)}
                                    >
                                        Hidden from Viewers
                                    </button>
                                </div>
                                <div className={classes.visibilityStateDesc}>
                                    {!hasViewers
                                        ? 'No viewers added yet.'
                                        : isShared
                                            ? 'New notes and recordings are visible to you, viewers, and co-owners. You can still hide any note individually.'
                                            : 'New notes and recordings stay between you and any co-owners. You choose what to share with viewers.'}
                                </div>

                                <div className={classes.listActionFooter}>
                                    <button
                                        className={classes.primaryBtn}
                                        onClick={() => setPage('add-person')}
                                        data-id="add-person-btn"
                                    >
                                        Invite Someone
                                    </button>
                                </div>
                            </>
                        )}

                        {page === 'add-person' && (
                            <AddPersonFlow
                                existingCoOwnerCount={coOwnersUsed}
                                onCancel={() => setPage('list')}
                                onAdd={handleAddPerson}
                                patientFirstName={patient.firstName || 'them'}
                            />
                        )}

                        {page === 'edit-care-map' && editingRow && (
                            <PrototypeCareMapEditDrawer
                                row={editingRow}
                                patientFirstName={patient.firstName || 'them'}
                                onClose={closeCareMapEdit}
                                onManageAccess={closeCareMapEdit}
                                onStubAction={stubAlert}
                            />
                        )}
                    </div>

                    <div className={classes.spikeFooter}>
                        <span className={classes.spikeBadge}>Prototype</span>
                        {/* Preset switcher only matters for what the list shows — hide on
                            the add-person page since toggling it there has no visible effect. */}
                        {page === 'list' && (
                            <div className={classes.miniSwitcher}>
                                <button
                                    className={`${classes.miniBtn} ${preset === 'brand-new' ? classes.miniBtnOn : ''}`}
                                    onClick={() => applyPreset('brand-new')}
                                >
                                    Brand-new
                                </button>
                                <button
                                    className={`${classes.miniBtn} ${preset === 'populated' ? classes.miniBtnOn : ''}`}
                                    onClick={() => applyPreset('populated')}
                                >
                                    Real
                                </button>
                                <button
                                    className={`${classes.miniBtn} ${preset === 'full' ? classes.miniBtnOn : ''}`}
                                    onClick={() => applyPreset('full')}
                                >
                                    Full
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </BottomSheetModal>

            {/* Project's standard info popover, anchored to the (i) button by id. */}
            <HeaderPopover
                trigger="access-info-trigger"
                isOpen={showAccessInfo}
                onDidDismiss={() => setShowAccessInfo(false)}
                maxWidth="default"
                title="Sharing Options"
                content={
                    <div className={classes.popoverBody}>
                        <div>
                            <span className={classes.popoverRole}>Viewer</span> — sees shared notes, recordings, and appointments, and can comment. Can't edit anything or see hidden info (hidden notes, recordings, medications, diagnoses).
                        </div>
                        <div style={{ marginTop: 10 }}>
                            <span className={classes.popoverRole}>Co-Owner</span> — a full partner. Sees everything, including hidden notes, recordings, medications, and diagnoses. Can add and edit notes, recordings, appointments, and tasks, and can invite and remove others. Two included in free.
                        </div>
                        <div style={{ marginTop: 10 }}>
                            <span className={classes.popoverRole}>Share link</span> — temporary access without needing an account. Sees shared notes, recordings, and appointments but can't comment.
                        </div>
                    </div>
                }
            />
        </>
    );
};

// Re-export the access type so other files can import from a single place.
export type { IAccessLevel };
